/**
 * spoken_time.ts · 09_APP · OS-006 · capability C36, decision D-0058
 *
 * Turns what Emily says (or types) about her hours into one action for os_work_said.
 * Emily, 2026-10-19: "there should be a microphone there so that I could just talk in the hours."
 *
 * It never saves anything. YourDay shows her the result and she presses Save, because a misheard
 * "two" as "ten" would otherwise land in an effort certification as fact.
 */

export type HoursAction = (
  | { action: "start"; start: Date; summary: string }
  | { action: "stop"; end: Date; summary: string }
  | { action: "stretch"; start: Date; end: Date; summary: string }
  | { action: "what"; summary: string }
) & { what: string };

/**
 * What she is working on, in her words. Emily, 2026-10-19: "give you an idea of what I'm working on
 * or what I've been working on, so you can list that as well." Kept close to verbatim; she can edit
 * it before saving.
 */
export function extractWhat(raw: string): string {
  const m = raw.match(/\b(?:working on|work on|worked on|been working on|focusing on|focused on|doing|busy with|on)\s+(.+)$/i);
  if (!m) return "";
  return m[1]!
    .replace(/\s*\b(?:since|from|at|until|till|to)\s+(?:\d{1,2}(?::\d{2})?|noon|now)\b.*$/i, "")
    .replace(/[.!?\s]+$/, "")
    .trim();
}

const WORDS: Record<string, string> = {
  one: "1", two: "2", three: "3", four: "4", five: "5", six: "6", seven: "7", eight: "8",
  nine: "9", ten: "10", eleven: "11", twelve: "12", fifteen: "15", thirty: "30",
  "forty five": "45", "forty-five": "45", fortyfive: "45",
};

export function fmt(d: Date): string {
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function normalize(raw: string): string {
  let s = " " + raw.toLowerCase().replace(/[.,!?]/g, " ").replace(/\s+/g, " ") + " ";
  s = s.replace(/\bnoon\b/g, "12 pm").replace(/\bmidday\b/g, "12 pm");
  s = s.replace(/\bo'?clock\b/g, " ");
  // "back from lunch" / "back from my break" is a START: the stop word is what she came back FROM.
  s = s.replace(/\b(back|returned|came back|got back) from (?:my |a |the )?(lunch|break|meeting|appointment|errand)s?\b/g, "$1");
  s = s.replace(/\ba m\b/g, "am").replace(/\bp m\b/g, "pm");
  for (const [w, n] of Object.entries(WORDS).sort((a, b) => b[0].length - a[0].length)) {
    s = s.replace(new RegExp(`\\b${w}\\b`, "g"), n);
  }
  // "half past 10" / "quarter past 10" / "quarter to 11"
  s = s.replace(/\bhalf past (\d{1,2})\b/g, "$1:30").replace(/\bquarter past (\d{1,2})\b/g, "$1:15");
  s = s.replace(/\bquarter (?:to|till) (\d{1,2})\b/g, (_m, h) => `${(Number(h) + 11) % 12 || 12}:45`);
  // "10 30" spoken as two numbers
  s = s.replace(/\b(\d{1,2}) (\d{2})\b(?! ?(?:minutes?|mins?))/g, "$1:$2");
  return s.replace(/\s+/g, " ");
}

const TIME = /\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b|\bnow\b|\bright now\b/g;

/** Resolve an hour with no am/pm to the most recent past occurrence today. */
function toDate(h: number, m: number, ampm: string | undefined, now: Date): Date | null {
  if (h > 23 || m > 59) return null;
  const at = (hh: number) => { const d = new Date(now); d.setHours(hh, m, 0, 0); return d; };
  if (ampm === "am") return at(h === 12 ? 0 : h);
  if (ampm === "pm") return at(h === 12 ? 12 : h + 12);
  if (h > 12) return at(h);
  const soon = now.getTime() + 15 * 60000;
  const cands = (h === 12 ? [12] : [h, h + 12]).map(at).filter((d) => d.getTime() <= soon);
  return cands.length ? cands[cands.length - 1]! : at(h === 12 ? 12 : h);
}

function times(s: string, now: Date): Array<{ at: Date; index: number }> {
  const out: Array<{ at: Date; index: number }> = [];
  for (const m of s.matchAll(TIME)) {
    if (m[0].includes("now")) { out.push({ at: new Date(now), index: m.index ?? 0 }); continue; }
    const d = toDate(Number(m[1]), Number(m[2] ?? 0), m[3], now);
    if (d) out.push({ at: d, index: m.index ?? 0 });
  }
  return out;
}

const START = /\b(start|started|starting|began|begin|beginning|back|came back|coming back|resum|clock(?:ed)? in|since|got going|logged on|signed on)/;
const STOP = /\b(stop|stopped|stopping|break|lunch|done|finish|finished|quit|quitting|end|ended|clock(?:ed)? out|leaving|left|pause|paused|wrap|wrapped|call(?:ed|ing)? it)/;

export function parseHours(raw: string, now: Date = new Date()): HoursAction | null {
  const s = normalize(raw);
  const ts = times(s, now);
  const what = extractWhat(raw);
  if (!ts.length) {
    return what ? { action: "what", what, summary: `Working on: ${what}` } : null;
  }

  const range = s.match(/\bfrom\b.*\b(?:to|until|till|through)\b/);
  if (range && ts.length >= 2) {
    const start = ts[0]!.at;
    const end = ts[1]!.at;
    if (end < start) end.setHours(end.getHours() + 12);
    return { action: "stretch", start, end, what, summary: `Worked ${fmt(start)} to ${fmt(end)}` };
  }

  const startAt = s.search(START);
  const stopAt = s.search(STOP);
  if (startAt < 0 && stopAt < 0) {
    // A time and what she is on, with no verb: "on the newsletter since 10" reads as a start.
    if (!what) return null;
  }
  // Whichever verb comes last before the time is what she meant: "I came back from lunch at 1".
  const t = ts[ts.length - 1]!;
  const lastStart = [...s.slice(0, t.index).matchAll(new RegExp(START, "g"))].pop()?.index ?? -1;
  const lastStop = [...s.slice(0, t.index).matchAll(new RegExp(STOP, "g"))].pop()?.index ?? -1;
  const isStart = lastStart >= 0 || lastStop >= 0 ? lastStart > lastStop : stopAt < 0;
  return isStart
    ? { action: "start", start: t.at, what, summary: `Started ${fmt(t.at)}` }
    : { action: "stop", end: t.at, what, summary: `Stopped ${fmt(t.at)}` };
}
